/**
 * Utilities for computing payment deadline countdown.
 *
 * Business rule: a session must be paid at least 12 hours before its start time.
 * Deadline = sessionStart - 12h
 */
import { BA_TIMEZONE } from './timezone';

export interface PaymentCountdownInfo {
  /** Full label for PatientDetailModal, e.g. "1 día restante para pagar" */
  label: string;
  /** Compact label for tight spaces, e.g. "1 día" */
  labelCompact: string;
  /** Hours-based label for detail views, e.g. "1 día y 4 horas restantes para pagar" */
  labelHours: string;
  /** Tooltip text oriented to the psychologist UX */
  tooltipLabel: string;
  /** Cancellation deadline label, e.g. "Si el paciente no abona antes del vie. 27 a las 07:30 se cancelará por falta de pago" */
  cancellationLabel: string;
  /**
   * New-format countdown shown in all UI locations.
   * e.g. "Le quedan 7 días y 19hs para pagar · Vence sáb. 23 a las 18:30"
   * e.g. "Le quedan 5hs para pagar · Vence hoy a las 18:30"
   * e.g. "Le queda menos de 1h para pagar · Vence hoy a las 18:30"
   */
  countdownLabel: string;
  /** True when the payment deadline has already passed */
  isExpired: boolean;
  /** True when less than 6 hours remain (triggers urgent styling) */
  isUrgent: boolean;
}

const PAYMENT_ADVANCE_MS = 12 * 60 * 60 * 1000; // 12 h in ms
const URGENT_THRESHOLD_MINS = 6 * 60; // 6 h in minutes

const WEEKDAY_ES: Record<string, string> = {
  Sun: 'dom.', Mon: 'lun.', Tue: 'mar.', Wed: 'mié.', Thu: 'jue.', Fri: 'vie.', Sat: 'sáb.',
};

/** Extracts date/time components of `date` as displayed in `timeZone` (never the browser's own timezone). */
function getZonedDateParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
    weekday: 'short',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    weekday: WEEKDAY_ES[get('weekday')] || '',
  };
}

function formatDeadline(deadline: Date, timeZone: string): string {
  const p = getZonedDateParts(deadline, timeZone);
  return `Si el paciente no abona antes del ${p.weekday} ${parseInt(p.day, 10)} a las ${p.hour}:${p.minute} se cancelará por falta de pago`;
}

/** "hoy a las HH:MM" or "[day]. [date] a las HH:MM", both computed in `timeZone` */
function formatDeadlineShort(deadline: Date, timeZone: string): string {
  const p = getZonedDateParts(deadline, timeZone);
  const now = getZonedDateParts(new Date(), timeZone);
  const isToday = p.year === now.year && p.month === now.month && p.day === now.day;
  if (isToday) return `hoy a las ${p.hour}:${p.minute}`;
  return `${p.weekday} ${parseInt(p.day, 10)} a las ${p.hour}:${p.minute}`;
}

/**
 * Computes payment countdown info relative to now.
 *
 * @param sessionStart - The exact datetime when the session starts (a real instant —
 *   callers must resolve any naive backend string via `parseBackendSessionDate` first).
 * @param timeZone - Timezone used to format the deadline day/hour (defaults to the
 *   Agenda's Buenos Aires timezone). Must match across all views so the same session
 *   always shows the same deadline, regardless of the viewer's device timezone.
 */
export function getPaymentCountdown(sessionStart: Date, timeZone: string = BA_TIMEZONE): PaymentCountdownInfo {
  // Defensive: some sessions arrive with an unparseable/missing raw date (e.g. a
  // malformed backend record). Treating it as "expired" hides the countdown badge
  // instead of throwing "Invalid time value" out of Intl.DateTimeFormat and taking
  // down the whole page (every caller already guards its render on `!isExpired`).
  if (Number.isNaN(sessionStart.getTime())) {
    return {
      label: 'Plazo de pago vencido',
      labelCompact: 'Vencido',
      labelHours: 'Plazo de pago vencido',
      tooltipLabel: 'El plazo de pago ya venció.',
      cancellationLabel: 'Plazo de pago vencido',
      countdownLabel: 'Plazo de pago vencido',
      isExpired: true,
      isUrgent: false,
    };
  }

  const deadline = new Date(sessionStart.getTime() - PAYMENT_ADVANCE_MS);
  const diffMs = deadline.getTime() - Date.now();

  if (diffMs <= 0) {
    return {
      label: 'Plazo de pago vencido',
      labelCompact: 'Vencido',
      labelHours: 'Plazo de pago vencido',
      tooltipLabel: 'El plazo de pago ya venció.',
      cancellationLabel: formatDeadline(deadline, timeZone),
      countdownLabel: 'Plazo de pago vencido',
      isExpired: true,
      isUrgent: false,
    };
  }

  const totalMins = Math.floor(diffMs / 60_000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  const days = Math.floor(hours / 24);
  const isUrgent = totalMins < URGENT_THRESHOLD_MINS;

  let label: string;
  let labelCompact: string;
  let labelHours: string;
  let timeText: string; // texto de tiempo para el tooltip

  if (days >= 1) {
    const dayLabel = days === 1 ? '1 día' : `${days} días`;
    label = `${dayLabel} restante${days === 1 ? '' : 's'} para pagar`;
    labelCompact = dayLabel;
    timeText = dayLabel;
    const remainingHours = hours % 24;
    if (remainingHours > 0) {
      labelHours = `${dayLabel} y ${remainingHours}hs para pagar`;
    } else {
      labelHours = `${dayLabel} para pagar`;
    }
  } else if (hours >= 1) {
    const hoursLabel = hours === 1 ? '1 hora' : `${hours} horas`;
    label = mins > 0
      ? `${hoursLabel} ${mins}m restantes para pagar`
      : `${hoursLabel} restantes para pagar`;
    labelCompact = `${hours}h`;
    labelHours = mins > 0 ? `${hours}hs ${mins}m para pagar` : `${hours}hs para pagar`;
    timeText = mins > 0 ? `${hoursLabel} y ${mins} minutos` : hoursLabel;
  } else {
    const minsLabel = mins === 1 ? '1 minuto' : `${mins} minutos`;
    label = `${minsLabel} restantes para pagar`;
    labelCompact = `${mins}m`;
    labelHours = label;
    timeText = minsLabel;
  }

  const tooltipLabel = `El paciente tiene ${timeText} para realizar el pago antes de que se cancele la sesión`;
  const cancellationLabel = formatDeadline(deadline, timeZone);

  // "X días y Yhs" with hours always < 24 (e.g. 187h remaining → "7 días y 19hs"), never a raw hour count.
  let remainingLabel = '';
  if (hours >= 1) {
    const remainingHoursInDay = hours % 24;
    if (days >= 1) {
      const dayLabel = days === 1 ? '1 día' : `${days} días`;
      remainingLabel = remainingHoursInDay > 0 ? `${dayLabel} y ${remainingHoursInDay}hs` : dayLabel;
    } else {
      remainingLabel = `${hours}hs`;
    }
  }
  const countdownLabel = hours >= 1
    ? `Le quedan ${remainingLabel} para pagar · Vence ${formatDeadlineShort(deadline, timeZone)}`
    : `Le queda menos de 1h para pagar · Vence ${formatDeadlineShort(deadline, timeZone)}`;

  return { label, labelCompact, labelHours, tooltipLabel, cancellationLabel, countdownLabel, isExpired: false, isUrgent };
}
